import { useMemo, useState } from "react";
import { Plus, RotateCcw, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { useSandboxDraft } from "../hooks/useSandboxDraft";
import { AddMachineDialog } from "../AddMachineDialog";
import { useSandboxFlow } from "./hooks/useSandboxFlow";
import { SandboxFlowSurface } from "./SandboxFlowSurface";

// Canvas por estágios: estágios viram lanes visuais desenhadas no próprio React Flow (header de
// nome/reorder/remover/+máquina embutido em cada lane — ver SandboxLaneNode) + máquinas
// arrastáveis livremente entre elas (onNodeDragStop no hook). Sinal de "rascunho
// alterado" + restaurar/limpar arranjo. A11y: a superfície tem `role="application"` +
// `aria-label` (abaixo). FOLLOW-UP conhecido — navegação por teclado (mover/selecionar nós sem
// mouse) e `aria-live` para anunciar mudanças de estágio/gargalo ainda não implementados; o React
// Flow não os cobre por padrão. Os controles de estágio (Input/botões) são HTML normal dentro do
// node, então seguem focáveis/teclado-ok mesmo embutidos no canvas.
interface Props {
  bottleneckStageKey?: string | null;
  /** Vazão por estágio da estimativa viva. */
  perStage?: Array<{ stageKey: string; throughputUnH: number }>;
  incompatibleKeys?: Set<string>;
  editable?: boolean;
  /** Descarta as edições do rascunho (re-hidrata linha, ou limpa se linha nova). */
  onRestore?: () => void;
  restoreLabel?: string;
}

export function SandboxCanvas({
  bottleneckStageKey,
  perStage,
  incompatibleKeys,
  editable = true,
  onRestore,
  restoreLabel = "Restaurar linha original",
}: Props) {
  const dirty = useSandboxDraft((s) => s.dirty);
  const productId = useSandboxDraft((s) => s.productId);
  const machines = useSandboxDraft((s) => s.machines);
  const addAcervoMachine = useSandboxDraft((s) => s.addAcervoMachine);
  // null = fechado; "" = aberto sem estágio-alvo fixo (entrada vazia/canvas vazio, cria o 1º
  // estágio); string = aberto com estágio-alvo fixo (botão "+ Máquina" de uma lane).
  const [addTarget, setAddTarget] = useState<string | null>(null);
  const flow = useSandboxFlow({
    bottleneckStageKey,
    perStage,
    incompatibleKeys,
    editable,
    onAddMachine: setAddTarget,
  });
  const presentIds = useMemo(
    () =>
      Object.values(machines)
        .filter((m) => m.present)
        .map((m) => m.sourceMachineId),
    [machines]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <Workflow className="h-4 w-4 text-muted-foreground" />
          Fluxo por estágios
          {dirty ? (
            <Pill tone="warning" withDot>
              Rascunho alterado
            </Pill>
          ) : null}
        </h2>
        {onRestore ? (
          <Button variant="ghost" size="sm" disabled={!dirty} onClick={onRestore}>
            <RotateCcw className="h-4 w-4" />
            {restoreLabel}
          </Button>
        ) : null}
      </div>

      {flow.isEmpty ? (
        <div
          role="application"
          aria-label="Canvas do cenário (vazio)"
          className="flex h-[320px] flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-card text-center"
        >
          <Workflow className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">Monte a linha do zero</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            Adicione máquinas do acervo para montar o fluxo por estágios. A 1ª rodada vira a
            referência.
          </p>
          {editable ? (
            <Button size="sm" variant="outline" className="mt-1" onClick={() => setAddTarget("")}>
              <Plus className="h-4 w-4" />
              Adicionar máquina do acervo
            </Button>
          ) : null}
        </div>
      ) : (
        <div
          role="application"
          aria-label="Canvas do cenário por estágios"
          className="h-[420px] rounded-md border bg-card"
        >
          <SandboxFlowSurface
            nodes={flow.rfNodes}
            edges={flow.rfEdges}
            editable={editable}
            onNodeClick={flow.onNodeClick}
            onNodesChange={flow.onNodesChange}
            onNodeDragStop={flow.onNodeDragStop}
            onPaneClick={flow.onPaneClick}
          />
        </div>
      )}

      {/* Entrada de máquina: canvas vazio/1º estágio OU "+ Máquina" de uma lane
       * (addTarget carrega o stageKey de destino nesse caso). */}
      <AddMachineDialog
        open={addTarget !== null}
        onOpenChange={(o) => setAddTarget(o ? addTarget : null)}
        productId={productId}
        presentSourceIds={presentIds}
        onAdd={(m) => addAcervoMachine({ ...m, stageKey: addTarget || undefined })}
      />
    </div>
  );
}
